<template>
  <SubscriptionPageGate
    :allowed-subscriptions="['enterprise']"
    title="Live Sessions requires an enterprise subscription."
    description="Upgrade to enterprise to monitor active conversations in real time."
  >
  <div class="live-sessions-container">
    <div class="mt-6 ml-4 flex space-between space-x-4 justify-between m-2 mr-4 flex-end">
      <div>
        <h1 class="text-2xl text-gray-900 dark:text-gray-200 font-medium">{{ t('liveSessions.title') }}</h1>
        <h1 class="text text-gray-900 dark:text-gray-400">{{ t('liveSessions.subtitle') }}</h1>
      </div>
      <div class="flex items-center space-x-2">
         <button 
            @click="requestLiveSessionsSnapshot"
            class="btn btn-ghost btn-sm btn-square" 
            :disabled="isSocketConnected !== true"
            :title="t('liveSessions.refresh')">
            <Icon icon="tabler:refresh" width="20" />
         </button>
         <span class="loading loading-ring loading-sm text-primary" v-if="isLoading !== false"></span>
         <span v-else class="text-xs text-gray-500 dark:text-gray-400">{{ t('liveSessions.updated', { time: formatDateTimeForViewer(lastUpdateTime) }) }} · {{ isSocketConnected === true ? 'Live' : 'Reconnecting' }}</span>
      </div>
    </div>

    <div v-if="isLoading === true && liveSessions.length === 0" class="text-center p-10">
        <span class="loading loading-dots loading-lg text-primary"></span>
    </div>
    <div v-else-if="liveSessions.length === 0 && initialLoadComplete === true" class="text-center p-10">
        <Icon icon="tabler:message-off" width="48" class="text-gray-400 mx-auto mb-4" />
        <p class="text-gray-500 dark:text-gray-400">{{ t('liveSessions.noActiveSessions') }}</p>
    </div>
    <div v-else class="live-sessions-grid p-4">
      <div v-for="session in liveSessions" :key="session.sessionid" class="live-session-item">
      <div
        class="card relative bg-base-100 dark:bg-gray-800 shadow-md hover:shadow-lg transition-shadow duration-200 cursor-pointer border dark:border-gray-700 overflow-hidden min-h-[190px]"
        @click="openConversationModal(session.sessionid)"
      >
        <span v-if="session.agenttype !== undefined && session.agenttype !== null && session.agenttype !== ''" 
              class="badge badge-sm absolute top-2 right-2 p-2 text-white"
              :class="{
                'badge-info': session.agenttype === 'PrivateAgent',
                'badge-warning': session.agenttype === 'HelpAgent',
                'badge-success': session.agenttype === 'PublicAgent'
              }">
          {{ t('liveSessions.agent') }}
        </span>
        <span v-if="session.hashumanintheloop === true"
              class="badge badge-sm absolute top-2 left-2 p-2 badge-warning text-black">
          Human Loop
        </span>

        <div class="card-body p-5 mt-4">
          <h2 class="card-title text-base font-semibold truncate flex items-center mb-1">
            <Icon icon="tabler:robot" width="30" class="mr-2 flex-shrink-0 text-primary" title="AI Agent" />
            <Icon v-if="session.channel === 'phone'" icon="tabler:phone" width="24" class="mr-2 flex-shrink-0 text-secondary" title="Phone" />
            <Icon v-else-if="session.channel === 'whatsapp'" icon="tabler:brand-whatsapp" width="24" class="mr-2 flex-shrink-0 text-green-500" title="WhatsApp" />
            <Icon v-else-if="session.channel === 'email'" icon="tabler:mail" width="24" class="mr-2 flex-shrink-0 text-secondary" title="Email" />
            <Icon v-else icon="tabler:world" width="24" class="mr-2 flex-shrink-0 text-secondary" title="Web" />
            <span class="truncate text-xl" :title="isDefinedAndNotEmpty(session.agentname) != false ? session.agentname : 'Unknown Agent'">{{ isDefinedAndNotEmpty(session.agentname) != false ? session.agentname : 'Unknown Agent' }}</span>
          </h2>
          
          <p class="text-xs text-gray-600 dark:text-gray-400 truncate flex items-center mb-2" :title="session.sessionid">
            <Icon icon="tabler:id" class="mr-1 flex-shrink-0" />
            <span class="truncate">{{ session.sessionid }}</span>
          </p>
          <div class="session-stats">
            <div class="session-stat">
              <span class="session-stat-label">Duration</span>
              <span class="session-stat-value">{{ formatSessionDuration(session) }}</span>
            </div>
            <div class="session-stat">
              <span class="session-stat-label">Messages</span>
              <span class="session-stat-value">{{ getSessionMessageCount(session) }}</span>
            </div>
            <div class="session-stat">
              <span class="session-stat-label">Last activity</span>
              <span class="session-stat-value">{{ formatRelativeTime(session.lastmessageat) }}</span>
            </div>
          </div>
          <div class="card-actions justify-end mt-3">
            <button
              class="btn btn-xs btn-outline btn-error"
              @click.stop="closeLiveConversation(session)"
              :disabled="isSessionClosing(session.sessionid) === true"
            >
              <span v-if="isSessionClosing(session.sessionid) === true" class="loading loading-spinner loading-xs"></span>
              <span v-else>Close conversation</span>
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>

    <dialog :open="isConversationModalOpen" class="modal modal-bottom sm:modal-middle">
        <div class="modal-box max-w-4xl bg-base-100 dark:bg-gray-800">
            <div class="sticky top-0 bg-base-100 dark:bg-gray-800 p-4 z-10 border-b dark:border-gray-700 flex justify-between items-center">
                 <h3 class="font-bold text-lg">{{ t('liveSessions.conversation') }}: {{ selectedSessionId }}</h3>
                 <div class="flex items-center space-x-2">
                    <button
                      class="btn btn-sm btn-outline btn-error"
                      @click="closeSelectedConversation"
                      :disabled="isSelectedSessionClosing === true"
                    >
                      <span v-if="isSelectedSessionClosing === true" class="loading loading-spinner loading-xs"></span>
                      <span v-else>Close conversation</span>
                    </button>
                    <button @click="closeConversationModal" class="btn btn-sm btn-circle btn-ghost">{{ t('liveSessions.close') }}</button>
                 </div>
            </div>

            <div ref="chatHistoryContainer" class="p-4 max-h-[65vh] overflow-y-auto bg-base-200 dark:bg-gray-700 rounded mb-4">
                <div v-if="isLoadingConversation === true && conversationHistory.length === 0" class="text-center p-6">
                    <span class="loading loading-spinner loading-lg text-primary"></span>
                </div>
                <div v-else-if="conversationHistory.length > 0" class="space-y-4">
                    <template v-for="(turn, index) in conversationHistory" :key="getConversationTurnKey(turn, index)">
                 
                        <div class="chat chat-end" v-if="hasMessageText(turn.userquery) === true" :style="{ direction: getTextDirection(turn.userquery) }">
                            <div class="chat-header text-xs opacity-50 mb-1">{{ t('liveSessions.user') }}
                               <time class="text-xs opacity-50 ml-1 hidden" :title="formatDateTimeForViewer(turn.createdat)">{{ formatRelativeTime(turn.createdat) }}</time>
                            </div>
                            <div class="chat-bubble chat-bubble-primary" v-html="escapeHtml(turn.userquery)"></div>
                        </div>
                       
                         <div v-if="hasMessageText(turn.response) === true" class="chat chat-start" :style="{ direction: getTextDirection(turn.response) }">
                             <div class="chat-header text-xs opacity-50 mb-1">{{ t('liveSessions.agent') }}
                                <time class="text-xs opacity-50 ml-1 hidden" :title="formatDateTimeForViewer(turn.createdat)">{{ formatRelativeTime(turn.createdat) }}</time>
                             </div>
                             <div class="chat-bubble bg-white dark:bg-gray-600 text-gray-900 dark:text-white" v-html="sanitizeHtml(turn.response)"></div>
                             <details v-if="hasLatency(turn) === true" class="latency-details">
                               <summary class="latency-summary">{{ formatLatencyMs(getLatencyTotalMs(turn)) }}</summary>
                               <div class="latency-popover">
                                 <div v-for="stage in getLatencyStages(turn)" :key="stage.key" class="latency-stage">
                                   <span class="latency-stage-label">{{ stage.label }}</span>
                                   <span class="latency-stage-bar-wrap">
                                     <span class="latency-stage-bar" :style="{ width: stage.width, backgroundColor: stage.color }"></span>
                                   </span>
                                   <span class="latency-stage-value">{{ formatLatencyMs(stage.value) }}</span>
                                 </div>
                                 <div class="latency-total">
                                   <span>Total</span>
                                   <strong>{{ formatLatencyMs(getLatencyTotalMs(turn)) }}</strong>
                                 </div>
                                 <div v-if="getAudioProgressText(turn) !== ''" class="latency-audio-progress">{{ getAudioProgressText(turn) }}</div>
                               </div>
                             </details>
                         </div>
                    </template>
                     <div v-if="isLoadingConversation === true && conversationHistory.length > 0" class="text-center pt-4">
                         <span class="loading loading-ring loading-sm text-primary"></span>
                     </div>
                </div>
                <div v-else class="text-center p-6">
                    <p class="text-gray-500 dark:text-gray-400">{{ t('liveSessions.noConversationHistory') }}</p>
                </div>
            </div>
            <div class="modal-action mt-4 sticky bottom-0 bg-base-100 dark:bg-gray-800 py-3 border-t dark:border-gray-700">
                <button class="btn" @click="closeConversationModal">{{ t('liveSessions.close') }}</button>
            </div>
        </div>
         <form method="dialog" class="modal-backdrop">
           <button @click="closeConversationModal">{{ t('liveSessions.close') }}</button>
         </form>
      </dialog>
  </div>
  </SubscriptionPageGate>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick, computed } from 'vue';
import axios from '../api/axiosInstance'; 
import { useToast } from 'vue-toastification';
import { useRouter } from 'vue-router';
import { globalState } from '@/globalState';
import { Icon } from '@iconify/vue/dist/offline';
import { useI18n } from '@/i18n';
import SubscriptionPageGate from '@/components/SubscriptionPageGate.vue';
import { isDefinedAndNotEmpty } from '../../isDefinedAndNotEmpty.js';
import { formatDateTimeForViewer } from '@/shared/utils/dateTime';
import { sanitizeHtml } from '@/shared/utils/sanitizeHtml';
const { t } = useI18n();

const toast = useToast();
const router = useRouter();

const liveSessions = ref([]);
const isLoading = ref(false);
const initialLoadComplete = ref(false);
const lastUpdateTime = ref(new Date());
const isSocketConnected = ref(false);
const liveSessionsSocket = ref(null);
const socketReconnectTimer = ref(null);
const socketReconnectDelay = 3000;
const closeRequestResolvers = new Map();

const isConversationModalOpen = ref(false);
const selectedSessionId = ref(null);
const conversationHistory = ref([]);
const isLoadingConversation = ref(false);
const conversationPollInterval = ref(null);
const conversationPollIntervalDelay = 4000;
const chatHistoryContainer = ref(null);
const closingSessionIds = ref([]);
const conversationFetchesInFlight = new Set();
let conversationRequestVersion = 0;
let closeTimer = null;
const minutesToCloseConversation = 30;
const isSelectedSessionClosing = ref(false);
const isEnterpriseSubscription = computed(() => {
  const subscription = typeof globalState.userInfo?.subscription === 'string' ? globalState.userInfo.subscription.trim().toLowerCase() : '';
  return subscription === 'enterprise';
});

const getAuthToken = () => {
  if (globalState.authUsesHttpOnlyCookies === true) {
    return '';
  }
  const readCookieValue = (name) => {
    const target = `${encodeURIComponent(name)}=`;
    const cookies = document.cookie.split('; ');
    for (let index = 0; index < cookies.length; index += 1) {
      const row = cookies[index];
      if (typeof row === 'string' && row.startsWith(target) === true) {
        const rawValue = row.slice(target.length);
        try {
          return decodeURIComponent(rawValue);
        } catch (error) {
          return rawValue;
        }
      }
    }
    return '';
  };
  const authToken = readCookieValue('authToken').trim();
  if (authToken !== '') {
    return authToken;
  }
  const jwtToken = readCookieValue('jwtToken').trim();
  if (jwtToken !== '') {
    return jwtToken;
  }
  return '';
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json'
  };
  if (token !== '') {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};
const getLiveSessionsSocketUrl = () => {
  const endpoint = typeof globalState.apiEndpoint === 'string' ? globalState.apiEndpoint.trim() : '';
  if (endpoint === '') {
    return '';
  }
  const sanitizedEndpoint = endpoint.endsWith('/') === true ? endpoint.slice(0, -1) : endpoint;
  const wsBase = sanitizedEndpoint.startsWith('https://') === true
    ? `wss://${sanitizedEndpoint.slice('https://'.length)}`
    : (sanitizedEndpoint.startsWith('http://') === true
      ? `ws://${sanitizedEndpoint.slice('http://'.length)}`
      : sanitizedEndpoint);
  return `${wsBase}/api/reports/liveSessions/ws`;
};

const handleAuthError = (error) => {
  if (error.response?.status === 401 || error.response?.status === 403) {
    toast.error('Authentication error. Redirecting to login.');
    cleanupLiveSessionsSocketConnection(true);
    if (conversationPollInterval.value !== null) clearInterval(conversationPollInterval.value);
    router.push('/login');
  } else {
    toast.error(isDefinedAndNotEmpty(error.response?.data?.message) != false ? error.response.data.message : (isDefinedAndNotEmpty(error.message) != false ? error.message : 'An unexpected error occurred.'));
    console.error('API Error:', error);
  }
};

const handleSocketSnapshot = (sessionsPayload) => {
  const rows = Array.isArray(sessionsPayload) === true ? sessionsPayload : [];
  liveSessions.value = rows;
  lastUpdateTime.value = new Date();
  isLoading.value = false;
  initialLoadComplete.value = true;
};

const cleanupLiveSessionsSocketConnection = (skipReconnect) => {
  if (socketReconnectTimer.value !== null) {
    clearTimeout(socketReconnectTimer.value);
    socketReconnectTimer.value = null;
  }
  const ws = liveSessionsSocket.value;
  liveSessionsSocket.value = null;
  isSocketConnected.value = false;
  if (ws !== null) {
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    if (ws.readyState === 0 || ws.readyState === 1) {
      ws.close();
    }
  }
  if (skipReconnect === true) {
    for (const resolverItem of closeRequestResolvers.values()) {
      resolverItem.resolve({ success: false, message: 'Socket disconnected' });
      clearTimeout(resolverItem.timerId);
    }
    closeRequestResolvers.clear();
  }
};

const scheduleSocketReconnect = () => {
  if (socketReconnectTimer.value !== null) {
    clearTimeout(socketReconnectTimer.value);
  }
  socketReconnectTimer.value = setTimeout(() => {
    connectLiveSessionsSocket();
  }, socketReconnectDelay);
};

const handleLiveSessionsSocketMessage = (data) => {
  const messageType = typeof data?.type === 'string' ? data.type : '';
  if (messageType === 'liveSessionsSnapshot') {
    handleSocketSnapshot(data.sessions);
    return;
  }
  if (messageType === 'closeConversationResult') {
    const requestId = typeof data.requestId === 'string' ? data.requestId : '';
    if (requestId !== '' && closeRequestResolvers.has(requestId) === true) {
      const resolverItem = closeRequestResolvers.get(requestId);
      clearTimeout(resolverItem.timerId);
      closeRequestResolvers.delete(requestId);
      resolverItem.resolve(data);
    }
    return;
  }
  if (messageType === 'error' && typeof data.message === 'string' && data.message !== '') {
    if (data.message.indexOf('Authentication required') >= 0) {
      isLoading.value = false;
      initialLoadComplete.value = true;
    }
    toast.error(data.message);
  }
};

const connectLiveSessionsSocket = () => {
  if (isEnterpriseSubscription.value !== true) {
    cleanupLiveSessionsSocketConnection(true);
    isLoading.value = false;
    liveSessions.value = [];
    initialLoadComplete.value = true;
    return;
  }
  cleanupLiveSessionsSocketConnection(true);
  const socketUrl = getLiveSessionsSocketUrl();
  if (socketUrl === '') {
    toast.error('Socket endpoint is not configured.');
    isLoading.value = false;
    return;
  }
  isLoading.value = true;
  const ws = new WebSocket(socketUrl);
  liveSessionsSocket.value = ws;
  ws.onopen = () => {
    isSocketConnected.value = true;
    const token = getAuthToken();
    if (token !== '') {
      ws.send(JSON.stringify({ type: 'authenticate', token: token }));
    }
  };
  ws.onmessage = (event) => {
    try {
      const data = JSON.parse(event.data);
      handleLiveSessionsSocketMessage(data);
    } catch (e) {}
  };
  ws.onerror = () => {
    isSocketConnected.value = false;
  };
  ws.onclose = () => {
    isSocketConnected.value = false;
    isLoading.value = false;
    if (initialLoadComplete.value !== true) {
      initialLoadComplete.value = true;
    }
    if (isConversationModalOpen.value === true) {
      return;
    }
    scheduleSocketReconnect();
  };
};

const requestLiveSessionsSnapshot = () => {
  if (isEnterpriseSubscription.value !== true) {
    return;
  }
  const ws = liveSessionsSocket.value;
  if (ws === null || ws.readyState !== 1) {
    connectLiveSessionsSocket();
    return;
  }
  ws.send(JSON.stringify({ type: 'requestSnapshot' }));
};

const sendCloseConversationOverSocket = async (sessionId, agentId) => {
  const ws = liveSessionsSocket.value;
  if (ws === null || ws.readyState !== 1) {
    return { success: false, message: 'Live socket is disconnected.' };
  }
  const requestId = `${sessionId}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const payload = {
    type: 'closeConversation',
    requestId,
    sessionId,
    agentId: agentId === '' ? null : agentId
  };
  const responsePromise = new Promise((resolve) => {
    const timerId = setTimeout(() => {
      if (closeRequestResolvers.has(requestId) === true) {
        closeRequestResolvers.delete(requestId);
      }
      resolve({ success: false, message: 'Close request timed out.' });
    }, 9000);
    closeRequestResolvers.set(requestId, { resolve, timerId });
  });
  ws.send(JSON.stringify(payload));
  return await responsePromise;
};

const getConversationCursor = () => {
  for (let index = conversationHistory.value.length - 1; index >= 0; index = index - 1) {
    const createdAt = conversationHistory.value[index]?.createdat;
    const timestamp = new Date(createdAt);
    if (Number.isNaN(timestamp.getTime()) === false) {
      return timestamp.toISOString();
    }
  }
  return '';
};

const getConversationTurnIdentity = (turn) => {
  const messageId = turn?.messageid;
  if (messageId !== undefined && messageId !== null && String(messageId).trim() !== '') {
    return `message:${String(messageId)}`;
  }
  const createdAt = turn?.createdat !== undefined && turn?.createdat !== null ? String(turn.createdat) : '';
  const userQuery = typeof turn?.userquery === 'string' ? turn.userquery : '';
  const response = typeof turn?.response === 'string' ? turn.response : '';
  return `content:${createdAt}:${userQuery}:${response}`;
};

const getConversationTurnKey = (turn, index) => {
  return `${selectedSessionId.value}:${getConversationTurnIdentity(turn)}:${index}`;
};

const mergeConversationHistory = (incomingRows) => {
  const nextRows = conversationHistory.value.slice();
  const rowIndexes = new Map();
  for (let index = 0; index < nextRows.length; index = index + 1) {
    rowIndexes.set(getConversationTurnIdentity(nextRows[index]), index);
  }
  let changed = false;
  for (let index = 0; index < incomingRows.length; index = index + 1) {
    const incomingRow = incomingRows[index];
    const identity = getConversationTurnIdentity(incomingRow);
    if (rowIndexes.has(identity) === true) {
      const existingIndex = rowIndexes.get(identity);
      if (JSON.stringify(nextRows[existingIndex]) !== JSON.stringify(incomingRow)) {
        nextRows[existingIndex] = incomingRow;
        changed = true;
      }
      continue;
    }
    rowIndexes.set(identity, nextRows.length);
    nextRows.push(incomingRow);
    changed = true;
  }
  if (changed === true) {
    conversationHistory.value = nextRows;
  }
  return changed;
};

const fetchConversationDetails = async (sessionId, incremental) => {
  if (sessionId === null) return;
  const safeSessionId = String(sessionId);
  if (conversationFetchesInFlight.has(safeSessionId) === true) { return; }
  if (incremental === true && document.visibilityState !== 'visible') { return; }
  conversationFetchesInFlight.add(safeSessionId);
  const requestVersion = conversationRequestVersion;
  if (incremental !== true) {
    isLoadingConversation.value = true;
  }
  const headers = getAuthHeaders();
   if (headers === null) {
       isLoadingConversation.value = false;
       conversationFetchesInFlight.delete(safeSessionId);
       closeConversationModal();
       return;
   }

  try {
    const cursor = incremental === true ? getConversationCursor() : '';
    const requestConfig = { headers };
    if (cursor !== '') {
      requestConfig.params = { since: cursor };
    }
    const response = await axios.get(`${globalState.apiEndpoint}/api/reports/conversation/${safeSessionId}`, requestConfig);
    if (requestVersion !== conversationRequestVersion) { return; }
    if (selectedSessionId.value !== sessionId) { return; }
    if (isConversationModalOpen.value !== true) { return; }
    const shouldScroll = isScrolledToBottom();
    const payload = response !== null && response !== undefined && typeof response.data === 'object' && response.data !== null && Array.isArray(response.data) !== true
      ? response.data
      : {};
    const incomingRows = Array.isArray(payload.history) === true ? payload.history : (Array.isArray(response.data) === true ? response.data : []);
    const changed = incremental === true ? mergeConversationHistory(incomingRows) : true;
    if (incremental !== true) {
      conversationHistory.value = incomingRows;
    }
    if (changed === true) {
      await nextTick();
    }
    if(shouldScroll === true && changed === true) {
        scrollToBottom();
    }
  } catch (error) {
    toast.error('Failed to load or refresh conversation history.');
    console.error('Error loading conversation:', error);
    handleAuthError(error);
  } finally {
    conversationFetchesInFlight.delete(safeSessionId);
    if (requestVersion === conversationRequestVersion) {
      isLoadingConversation.value = false;
    }
  }
}

const isSessionClosing = (sessionId) => {
  if (typeof sessionId !== 'string' || sessionId === '') {
    return false;
  }
  return closingSessionIds.value.includes(sessionId) === true;
};

const closeLiveConversation = async (session) => {
  const safeSessionId = typeof session?.sessionid === 'string' ? session.sessionid.trim() : '';
  const safeAgentId = typeof session?.agentid === 'string' ? session.agentid.trim() : '';
  if (safeSessionId === '') {
    toast.error('Invalid session data.');
    return;
  }
  if (isSessionClosing(safeSessionId) === true) {
    return;
  }
  closingSessionIds.value = [...closingSessionIds.value, safeSessionId];
  try {
    const result = await sendCloseConversationOverSocket(safeSessionId, safeAgentId);
    if (result?.success !== true) {
      toast.error(typeof result?.message === 'string' && result.message !== '' ? result.message : 'Failed to close conversation.');
      return;
    }
    liveSessions.value = liveSessions.value.filter(item => item.sessionid !== safeSessionId);
    if (selectedSessionId.value === safeSessionId) {
      closeConversationModal();
    }
    toast.success('Conversation closed successfully.');
    requestLiveSessionsSnapshot();
  } catch (error) {
    toast.error('Failed to close conversation.');
  } finally {
    closingSessionIds.value = closingSessionIds.value.filter(id => id !== safeSessionId);
  }
};

const closeSelectedConversation = async () => {
  const safeSelectedSessionId = typeof selectedSessionId.value === 'string' ? selectedSessionId.value.trim() : '';
  if (safeSelectedSessionId === '') {
    toast.error('Invalid session data.');
    return;
  }
  const matchedSession = liveSessions.value.find(item => item.sessionid === safeSelectedSessionId);
  isSelectedSessionClosing.value = true;
  try {
    await closeLiveConversation({
      sessionid: safeSelectedSessionId,
      agentid: matchedSession?.agentid
    });
  } finally {
    isSelectedSessionClosing.value = false;
  }
};

const openConversationModal = async (sessionId) => {
  if (isEnterpriseSubscription.value !== true) {
    return;
  }
  if (sessionId === undefined || sessionId === null) {
    toast.error('Invalid session data.');
    return;
  }
  selectedSessionId.value = sessionId;
  isConversationModalOpen.value = true;
  conversationHistory.value = [];
  conversationRequestVersion = conversationRequestVersion + 1;
  const requestVersion = conversationRequestVersion;
  await fetchConversationDetails(sessionId, false);
  if (requestVersion !== conversationRequestVersion) { return; }
  if (isConversationModalOpen.value !== true) { return; }
  if (selectedSessionId.value !== sessionId) { return; }

  if (conversationPollInterval.value !== null) {
      clearInterval(conversationPollInterval.value);
  }
  conversationPollInterval.value = setInterval(() => {
      fetchConversationDetails(selectedSessionId.value, true);
  }, conversationPollIntervalDelay);
  if (closeTimer != null) {
      clearTimeout(closeTimer);
  }
  closeTimer = setTimeout(() => {
      closeConversationModal();
  }, minutesToCloseConversation * 60 * 1000);
}

const closeConversationModal = () => {
  conversationRequestVersion = conversationRequestVersion + 1;
  isConversationModalOpen.value = false;
  selectedSessionId.value = null;
  conversationHistory.value = [];
  if (conversationPollInterval.value !== null) {
    clearInterval(conversationPollInterval.value);
    conversationPollInterval.value = null;
  }
  if (closeTimer != null) {
      clearTimeout(closeTimer);
      closeTimer = null;
  }
}

const scrollToBottom = () => {
  const container = chatHistoryContainer.value;
  if (container !== null) {
    container.scrollTop = container.scrollHeight;
  }
};

const isScrolledToBottom = () => {
    const container = chatHistoryContainer.value;
    if (container === null) return true;
    return container.scrollHeight - container.scrollTop - container.clientHeight < 50;
};

function formatRelativeTime(dateString) {
    if (dateString === null || dateString === undefined) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date'; 
    
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const weeks = Math.floor(days / 7);
    const months = Math.floor(days / 30);
    const years = Math.floor(days / 365);

    if (seconds < 60) {
        return `now`;
    } else if (minutes < 60) {
        return `${minutes} min ago`;
    } else if (hours < 24) {
        return `${hours} hr ago`;
    } else if (days < 7) {
        return `${days} day${days > 1 ? 's' : ''} ago`;
    } else if (weeks < 4) {
         return `${weeks} wk${weeks > 1 ? 's' : ''} ago`;
    } else if (months < 12) {
        return `${months} mo ago`;
    } else {
        return `${years} yr ago`;
    }
}

function formatDuration(secondsInput) {
    const totalSeconds = typeof secondsInput === 'number' ? Math.max(0, Math.floor(secondsInput)) : 0;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
}

function formatSessionDuration(session) {
  const rawDuration = Number(session?.durationseconds);
  if (Number.isNaN(rawDuration) === false && rawDuration >= 0) {
    return `${Math.floor(rawDuration / 60)} min`;
  }
  const firstTime = new Date(session?.firstmessageat).getTime();
  const lastTime = new Date(session?.lastmessageat).getTime();
  if (Number.isNaN(firstTime) === true || Number.isNaN(lastTime) === true || lastTime < firstTime) {
    return 'N/A';
  }
  const diffSeconds = Math.floor((lastTime - firstTime) / 1000);
  return `${Math.floor(diffSeconds / 60)} min`;
}

function getSessionMessageCount(session) {
  const parsed = Number(session?.messagecount);
  if (Number.isNaN(parsed) === true || parsed < 0) {
    return 0;
  }
  return Math.floor(parsed);
}

function getTextDirection(text) {
  if (text === null || text === undefined || text.length < 1) {
    return 'ltr';
  }
  const firstChar = text.charCodeAt(0);
  if ((firstChar >= 0x0590 && firstChar <= 0x05FF) || (firstChar >= 0x0600 && firstChar <= 0x06FF)) {
    return 'rtl';
  } else {
    return 'ltr';
  }
}

function hasMessageText(text) {
  return typeof text === 'string' && text.trim().length > 0;
}

function normalizeLatencyMs(value) {
  const parsed = Number(value);
  if (Number.isFinite(parsed) === true && parsed >= 0) {
    return Math.floor(parsed);
  }
  return 0;
}

function getLatencyObject(turn) {
  if (turn === undefined) { return null; }
  if (turn === null) { return null; }
  if (turn.latency === undefined) { return null; }
  if (turn.latency === null) { return null; }
  if (typeof turn.latency !== 'object') { return null; }
  return turn.latency;
}

function getLatencyTotalMs(turn) {
  const latency = getLatencyObject(turn);
  if (latency === null) { return 0; }
  const direct = normalizeLatencyMs(latency.totalMs);
  if (direct > 0) { return direct; }
  const stages = getLatencyStages(turn);
  let total = 0;
  for (let index = 0; index < stages.length; index = index + 1) {
    if (stages[index].key !== 'firstSentenceMs') {
      total = total + stages[index].value;
    }
  }
  return total;
}

function hasLatency(turn) {
  return getLatencyTotalMs(turn) > 0;
}

function formatLatencyMs(value) {
  const ms = normalizeLatencyMs(value);
  return `${ms}ms`;
}

function getLatencyStageDefinitions() {
  return [
    { key: 'transportInMs', label: 'Transport In', color: '#607d8b' },
    { key: 'vadDelayMs', label: 'VAD Delay', color: '#e45757' },
    { key: 'sttFinalizeMs', label: 'STT Finalize', color: '#5b9ee5' },
    { key: 'llmProcessingMs', label: 'LLM Processing', color: '#f4a23a' },
    { key: 'firstSentenceMs', label: 'First Sentence', color: '#d4c942' },
    { key: 'ttsSynthesisMs', label: 'TTS Synthesis', color: '#61b15a' },
    { key: 'transportOutMs', label: 'Transport Out', color: '#607d8b' }
  ];
}

function getLatencyStages(turn) {
  const latency = getLatencyObject(turn);
  if (latency === null) { return []; }
  const definitions = getLatencyStageDefinitions();
  const output = [];
  let maxValue = 1;
  for (let index = 0; index < definitions.length; index = index + 1) {
    const definition = definitions[index];
    const value = normalizeLatencyMs(latency[definition.key]);
    if (value <= 0) { continue; }
    if (value > maxValue) { maxValue = value; }
    output.push({
      key: definition.key,
      label: definition.label,
      value: value,
      color: definition.color,
      width: '0%'
    });
  }
  for (let index = 0; index < output.length; index = index + 1) {
    output[index].width = `${Math.max(8, Math.round((output[index].value / maxValue) * 100))}%`;
  }
  return output;
}

function getAudioProgressText(turn) {
  const latency = getLatencyObject(turn);
  if (latency === null) { return ''; }
  const finalMs = normalizeLatencyMs(latency.finalAudioProcMs);
  const totalMs = normalizeLatencyMs(latency.totalAudioProcMs);
  if (finalMs <= 0) {
    if (totalMs <= 0) { return ''; }
  }
  return `Audio processed: final ${formatLatencyMs(finalMs)}, total ${formatLatencyMs(totalMs)}`;
}

function escapeHtml(unsafe) {
  if (unsafe === null || unsafe === undefined) return '';
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

onMounted(() => {
  if (isEnterpriseSubscription.value === true) {
    connectLiveSessionsSocket();
  }
});

onUnmounted(() => {
  cleanupLiveSessionsSocketConnection(true);
  if (conversationPollInterval.value !== null) {
    clearInterval(conversationPollInterval.value);
  }
  if (closeTimer != null) {
      clearTimeout(closeTimer);
  }
});

</script>

<style scoped>
.live-sessions-container {
  max-width: 1600px;
  margin: 0 auto;
}

.live-sessions-grid {
  display: flex;
  flex-wrap: wrap;
  margin-left: -0.5rem;
  margin-right: -0.5rem;
}

.live-session-item {
  width: 100%;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  padding-bottom: 1rem;
}

@media (min-width: 768px) {
  .live-session-item {
    width: 50%;
  }
}

@media (min-width: 1024px) {
  .live-session-item {
    width: 33.3333%;
  }
}

@media (min-width: 1280px) {
  .live-session-item {
    width: 25%;
  }
}

.card {
  border-radius: 8px;
}
.card-body {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
}

.session-stats {
  display: flex;
  justify-content: space-between;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(148, 163, 184, 0.25);
}

.session-stat {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.session-stat-label {
  font-size: 11px;
  color: rgb(107 114 128);
}

.session-stat-value {
  font-size: 12px;
  font-weight: 600;
  color: rgb(17 24 39);
}

.chat-header {
  box-shadow: none;
  background-color: transparent;
}

.chat-bubble {
  max-width: 80%;
  padding: 0.75rem 1rem;
  border-radius: 1rem;
  line-height: 1.5;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.chat-start .chat-bubble {
  border-bottom-right-radius: 0.25rem;
}

.chat-end .chat-bubble {
    background-color: #fff;
   border-bottom-left-radius: 0.25rem;
}

.latency-details {
  position: relative;
  display: inline-block;
  margin-top: 0.5rem;
}

.latency-summary {
  list-style: none;
  cursor: pointer;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.5);
  background: rgba(255, 255, 255, 0.86);
  color: #374151;
  font-size: 11px;
  padding: 0.1rem 0.55rem;
}

.latency-summary::-webkit-details-marker {
  display: none;
}

.latency-popover {
  position: absolute;
  z-index: 40;
  left: 0;
  top: 1.65rem;
  width: 310px;
  border-radius: 0.5rem;
  border: 1px solid rgba(148, 163, 184, 0.45);
  background: #ffffff;
  box-shadow: 0 18px 34px rgba(15, 23, 42, 0.18);
  padding: 0.65rem;
}

.latency-stage {
  display: flex;
  align-items: center;
  margin-bottom: 0.42rem;
}

.latency-stage-label {
  width: 105px;
  font-size: 11px;
  color: #6b7280;
}

.latency-stage-bar-wrap {
  flex: 1;
  height: 10px;
  background: #eef2f7;
  border-radius: 999px;
  overflow: hidden;
  margin-left: 0.45rem;
  margin-right: 0.45rem;
}

.latency-stage-bar {
  display: block;
  height: 100%;
  border-radius: 999px;
}

.latency-stage-value {
  width: 48px;
  text-align: right;
  font-size: 11px;
  color: #6b7280;
}

.latency-total {
  display: flex;
  justify-content: space-between;
  border-top: 1px solid rgba(148, 163, 184, 0.28);
  margin-top: 0.25rem;
  padding-top: 0.5rem;
  font-size: 12px;
  color: #4b5563;
}

.latency-audio-progress {
  margin-top: 0.35rem;
  font-size: 11px;
  color: #6b7280;
}

.chat-bubble :deep(p) {
  margin: 0;
  line-height: 1.5;
}

.chat-bubble :deep(a) {
  color: inherit;
  text-decoration: underline;
}
.chat-bubble::before{display:none;}
.chat-bubble :deep(pre), .chat-bubble :deep(code) {
  background-color: rgba(0,0,0,0.1);
  padding: 0.1em 0.3em;
  border-radius: 4px;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-all;
}

.modal-box {
   background-color: var(--fallback-b1, oklch(var(--b1) / 1));
}
</style> 